import { useEditorStore } from '../stores/editorStore.js';
import { useAvailableTemplateTokens } from '../hooks/useAvailableTemplateTokens.js';
import { scopedDefinitions, valueVariableNames } from '../lib/list-variables.js';
import { useQuery } from '@tanstack/react-query';
import { api } from '../hooks/api.js';
import { StepsInspector } from './steps-block/StepsInspector.js';
import { StatInspector } from './stat-block/StatInspector.js';
import { TableInspector } from './table-block/TableInspector.js';
import { HtmlBlockInspector } from './html-block/HtmlBlockInspector.js';
import { SignatureInspector } from './signature-block/SignatureInspector.js';
import { ImageInspector } from './image-block/ImageBlockInspector.js';
import { OtpInspector } from './otp-block/OtpInspector.js';
import { AvatarInspector } from './avatar-block/AvatarInspector.js';
import { ListInspector } from './list-block/ListInspector.js';
import {
  FONT_STYLE_VALUES,
  type BlockStyle,
  type BlockType,
  type FontStyle,
} from '@blogforge/shared';
import { BackgroundGradientField } from './ui/BackgroundGradientField.js';
import { BorderField } from './ui/BorderField.js';
import { SectionInspector } from './section-block/SectionInspector.js';
import { ButtonInspector } from './button-block/ButtonBlockInspector.js';
import { ColumnsBlockInspector } from './columns-block/ColumnBlockInspector.js';
import { Field } from './ui/Field.js';

export function BlockInspector({
  post,
  mode = 'post',
  variableNameOptions,
}: {
  post: Record<string, unknown> | undefined;
  mode?: 'post' | 'email' | 'block';
  /**
   * Variable names offered in the image/OTP pickers. By default they are the
   * workspace's available merge fields — the email's own `variableDefinition`
   * plus its audience attributes, minus the reserved system names; a surface
   * editing something that is not a document (the email footer, whose
   * variables are the system ones) passes its own list instead.
   */
  variableNameOptions?: string[] | undefined;
}) {
  const [selectedBlockId, path] = useEditorStore((s) => s.selectedBlockId) || [null, []];
  const blocks = useEditorStore((s) => s.blocks);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const getBlockInPath = useEditorStore((s) => s.getBlockInPath);
  const getBlockIndex = useEditorStore((s) => s.getBlockIndex);

  const { data, isLoading } = useQuery({
    queryKey: ['fonts'],
    queryFn: () => api.fonts.list(),
  });

  const block = selectedBlockId ? getBlockInPath(selectedBlockId, path) : null;

  // The email's own variables when this is an email; the hook adds the
  // workspace's audience attributes and removes reserved names, so the
  // pickers offer exactly what the inline `{{` picker does. `valueVariableNames`
  // is that same reading of the document: a list holds variables rather than a
  // value, so its key is never offered as a token.
  //
  // A block inside a list's item template is offered that list's fields as well
  // — the canvas passes the scope down its own tree, and this panel, which
  // reads the document instead of rendering it, resolves it from the selected
  // block's path.
  const documentVariables =
    mode === 'email' && post
      ? valueVariableNames(
          scopedDefinitions(post['variableDefinition'], path, (id, ancestors) =>
            getBlockInPath(id, ancestors),
          ),
        )
      : [];
  const { tokens: availableVariables } = useAvailableTemplateTokens(documentVariables, {
    // Audience attributes are per recipient, so they are only offered where
    // the document is an email — a post would build the token literally.
    includeAudienceAttributes: mode === 'email',
  });
  const blockIndex = selectedBlockId ? getBlockIndex(selectedBlockId) : -1;

  return (
    <div className="space-y-5">
      {!block ? (
        <div className="py-10 text-center">
          <p className="text-ink-soft text-sm font-medium">No block selected</p>
          <p className="text-muted mt-1 text-xs">Click a block on the canvas to style it</p>
        </div>
      ) : (
        <>
          <p className="kicker">
            <span className="bg-accent h-1.5 w-1.5 rounded-full" />
            {block.type} block
          </p>

          <div>
            <label className="field-label">Text color</label>
            <input
              title="Text color input"
              type="color"
              className="border-line bg-bg-soft h-9 w-full cursor-pointer rounded-lg border p-1"
              value={block.style?.color ?? '#1C2833'}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    style: { ...block.style, color: e.target.value },
                  },
                  path,
                )
              }
            />
          </div>

          <div>
            <label className="field-label">Background colour</label>
            <input
              title="Background color input"
              type="color"
              className="border-line bg-bg-soft h-9 w-full cursor-pointer rounded-lg border p-1"
              value={block.style?.backgroundColor ?? '#ffffff'}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    style: { ...block.style, backgroundColor: e.target.value },
                  },
                  path,
                )
              }
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Min Height" hint='e.g. "60vh" or "400px"'>
              <input
                className="field-input"
                type="text"
                value={block.style?.minHeight || ''}
                placeholder="60vh"
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    {
                      style: { ...block.style, minHeight: e.target.value },
                    },
                    path,
                  )
                }
              />
            </Field>

            <Field label="Max Width" hint='Content column width, e.g. "900px"'>
              <input
                className="field-input"
                type="text"
                value={block.style?.maxWidth || ''}
                placeholder="900px"
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    {
                      style: { ...block.style, maxWidth: e.target.value },
                    },
                    path,
                  )
                }
              />
            </Field>
          </div>

          <Field label="Line Height">
            <input
              className="field-input"
              type="text"
              value={block.style?.lineHeight || ''}
              placeholder="1.6"
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    style: { ...block.style, lineHeight: e.target.value },
                  },
                  path,
                )
              }
            />
          </Field>

          <Field label="Box shadow">
            <input
              className="field-input"
              type="text"
              value={block.style?.boxShadow || ''}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    style: { ...block.style, boxShadow: e.target.value },
                  },
                  path,
                )
              }
            />
          </Field>

          <Field label="Box shadow">
            <input
              className="field-input"
              type="text"
              value={block.style?.boxShadow || ''}
              onChange={(e) =>
                updateBlock(
                  block.id,
                  {
                    style: { ...block.style, boxShadow: e.target.value },
                  },
                  path,
                )
              }
            />
          </Field>

          {/* Sections own their background (colour / image / gradient / video)
              in SectionInspector, so the generic fields skip them to keep one
              control per value. */}
          {block.type !== 'section' && (
            <BackgroundGradientField
              id="block-bg-gradient"
              value={block.style?.backgroundGradient ?? ''}
              onChange={(backgroundGradient) =>
                updateBlock(block.id, { style: { ...block.style, backgroundGradient } }, path)
              }
              hint="Painted over the background colour, which stays as the fallback where a gradient cannot render."
            />
          )}

          <div>
            <label className="field-label">Text align</label>
            <div className="flex gap-1">
              {(['left', 'center', 'right'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() =>
                    updateBlock(
                      block.id,
                      {
                        style: { ...block.style, textAlign: a },
                      },
                      path,
                    )
                  }
                  className={`flex-1 rounded border py-1.5 text-xs capitalize transition-colors ${
                    block.style?.textAlign === a
                      ? 'bg-accent border-accent text-accent-ink shadow-sm'
                      : 'border-line text-muted hover:bg-bg-soft'
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          {block.type !== 'section' && (
            <div>
              <label className="field-label">Padding</label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    'paddingTop',
                    'paddingBottom',
                    'paddingLeft',
                    'paddingRight',
                  ] as (keyof BlockStyle)[]
                ).map((key) => {
                  const label = key.replace('padding', ''); // "Top" | "Bottom" | "Left" | "Right"
                  const value = block.style?.[key]?.replace('px', '') ?? '';

                  return (
                    <div key={key}>
                      <label className="field-label text-xs">{label}</label>
                      <input
                        title={`Padding ${label.toLowerCase()}`}
                        type="number"
                        min="0"
                        placeholder="0"
                        className="border-line bg-bg-soft h-9 w-full rounded-lg border p-1"
                        value={value}
                        onChange={(e) =>
                          updateBlock(
                            block.id,
                            {
                              style: {
                                ...block.style,
                                [key]: e.target.value ? `${e.target.value}px` : undefined,
                              },
                            },
                            path,
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="field-label">Radius</label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  'borderTopLeftRadius',
                  'borderTopRightRadius',
                  'borderBottomLeftRadius',
                  'borderBottomRightRadius',
                ] as (keyof BlockStyle)[]
              ).map((key) => {
                const label = key.replace('border', ''); // "Top" | "Bottom" | "Left" | "Right"
                const value = block.style?.[key]?.replace('px', '') ?? '';

                return (
                  <div key={key}>
                    <label className="field-label text-xs">{label}</label>
                    <input
                      title={`Radius ${label.toLowerCase()}`}
                      type="number"
                      min="0"
                      placeholder="0"
                      className="border-line bg-bg-soft h-9 w-full rounded-lg border p-1"
                      value={value}
                      onChange={(e) =>
                        updateBlock(
                          block.id,
                          {
                            style: {
                              ...block.style,
                              [key]: e.target.value ? `${e.target.value}px` : undefined,
                            },
                          },
                          path,
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {(mode !== 'email' || (['columns', 'heading'] as BlockType[]).includes(block.type)) && (
            <div>
              <label className="field-label">Margin</label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  ['marginTop', 'marginBottom', 'marginLeft', 'marginRight'] as (keyof BlockStyle)[]
                ).map((key) => {
                  const label = key.replace('margin', ''); // "Top" | "Bottom" | "Left" | "Right"
                  const value = block.style?.[key]?.replace('px', '') ?? '';

                  return (
                    <div key={key}>
                      <label className="field-label text-xs">{label}</label>
                      <input
                        title={`Margin ${label.toLowerCase()}`}
                        type="number"
                        min="0"
                        placeholder="0"
                        className="border-line bg-bg-soft h-9 w-full rounded-lg border p-1"
                        value={value}
                        onChange={(e) =>
                          updateBlock(
                            block.id,
                            {
                              style: {
                                ...block.style,
                                [key]: e.target.value ? `${e.target.value}px` : undefined,
                              },
                            },
                            path,
                          )
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* The divider *is* a border — one line, drawn in its background
              colour — so a border control here would only be a second, ignored
              way to set the same thing. */}
          {block.type !== 'divider' && (
            <BorderField
              border={block.style?.border}
              borderColor={block.style?.borderColor}
              onChange={({ border, borderColor }) =>
                updateBlock(block.id, { style: { ...block.style, border, borderColor } }, path)
              }
            />
          )}

          {path.length === 0 && blockIndex === 0 && block.type === 'html' && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={block.attrs?.isHeader ?? false}
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    {
                      attrs: { ...block.attrs, isHeader: e.target.checked },
                    },
                    path,
                  )
                }
              />
              Is Blog Header?
            </label>
          )}

          {path.length === 0 && blockIndex === blocks.length - 1 && block.type === 'html' && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={block.attrs?.isFooter ?? false}
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    {
                      attrs: { ...block.attrs, isFooter: e.target.checked },
                    },
                    path,
                  )
                }
              />
              Is Blog Footer?
            </label>
          )}

          {block.type === 'section' && (
            <>
              <SectionInspector
                block={block}
                path={path}
                variableNameOptions={variableNameOptions ?? availableVariables}
              />
              <div>
                <label className="field-label">Padding</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'] as const).map(
                    (side) => (
                      <div key={side} className="flex flex-col items-start gap-1.5">
                        <span className="text-muted w-6 text-xs capitalize">
                          {side.replace('padding', '')}
                        </span>
                        <input
                          title={`${side} input`}
                          type="number"
                          min={0}
                          max={128}
                          step={4}
                          className="border-line bg-bg-soft text-ink w-full rounded-lg border px-2 py-1.5 text-xs"
                          value={parseInt(block.style?.[side] || '0', 10)}
                          onChange={(e) =>
                            updateBlock(
                              block.id,
                              {
                                style: { ...block.style, [side]: `${e.target.value}px` },
                              },
                              path,
                            )
                          }
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div>
                <label className="field-label">Flex direction</label>
                <div className="flex gap-1">
                  {(['row', 'column'] as const).map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() =>
                        updateBlock(
                          block.id,
                          {
                            style: { ...block.style, flexDirection: a },
                          },
                          path,
                        )
                      }
                      className={`flex-1 rounded border py-1.5 text-xs capitalize transition-colors ${
                        block.style?.flexDirection === a
                          ? 'bg-accent border-accent text-accent-ink shadow-sm'
                          : 'border-line text-muted hover:bg-bg-soft'
                      }`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="field-label">Gap</label>
                <input
                  title="Gap"
                  type="number"
                  min={0}
                  max={144}
                  className="field-input"
                  placeholder="Default"
                  value={block.style?.gap ? Number(block.style.gap.replace('px', '')) : ''}
                  onChange={(e) =>
                    updateBlock(
                      block.id,
                      {
                        style: {
                          ...block.style,
                          gap: e.target.value ? `${e.target.value}px` : undefined,
                        },
                      },
                      path,
                    )
                  }
                />
              </div>
            </>
          )}

          {block.type === 'heading' && (
            <div>
              <label className="field-label">Heading level</label>
              <div className="flex gap-1">
                {([1, 2, 3, 4] as const).map((l) => (
                  <button
                    type="button"
                    key={l}
                    onClick={() =>
                      updateBlock(
                        block.id,
                        {
                          attrs: { ...block.attrs, level: l },
                        },
                        path,
                      )
                    }
                    className={`flex-1 rounded border py-1.5 text-xs transition-colors ${
                      block.attrs.level === l
                        ? 'bg-accent border-accent text-accent-ink shadow-sm'
                        : 'border-line text-muted hover:bg-bg-soft'
                    }`}
                  >
                    H{l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {block.type === 'callout' && (
            <div>
              <label className="field-label">Variant</label>
              <div className="grid grid-cols-2 gap-1">
                {(['info', 'warning', 'success', 'danger'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() =>
                      updateBlock(
                        block.id,
                        {
                          attrs: { ...block.attrs, variant: v },
                        },
                        path,
                      )
                    }
                    className={`rounded border py-1.5 text-xs capitalize transition-colors ${
                      block.attrs.variant === v
                        ? 'bg-accent border-accent text-accent-ink shadow-sm'
                        : 'border-line text-muted hover:bg-bg-soft'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {block.type === 'columns' && <></>}

          {(['button', 'paragraph', 'heading', 'quote', 'callout'] as BlockType[]).includes(
            block.type,
          ) && (
            <>
              <div>
                <label htmlFor="font-family" className="field-label">
                  Font family
                </label>
                <select
                  title="Select font family"
                  className="field-input"
                  value={block.style?.fontFamily ?? ''}
                  onChange={(e) =>
                    updateBlock(
                      block.id,
                      {
                        style: { ...block.style, fontFamily: e.target.value },
                      },
                      path,
                    )
                  }
                >
                  <option value="">Default</option>
                  {isLoading ? (
                    <option disabled>Loading fonts...</option>
                  ) : (
                    data?.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="font-family" className="field-label">
                  Font style
                </label>
                <select
                  title="Select font style"
                  className="field-input"
                  value={block.style?.fontStyle ?? ''}
                  onChange={(e) =>
                    updateBlock(
                      block.id,
                      {
                        style: { ...block.style, fontStyle: e.target.value as FontStyle },
                      },
                      path,
                    )
                  }
                >
                  <option value="">Default</option>

                  {FONT_STYLE_VALUES?.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="field-label">Font size</label>
                  <input
                    title="Font size"
                    type="number"
                    min={8}
                    max={144}
                    className="field-input"
                    placeholder="Default"
                    value={
                      block.style?.fontSize ? Number(block.style.fontSize.replace('px', '')) : ''
                    }
                    onChange={(e) =>
                      updateBlock(
                        block.id,
                        {
                          style: {
                            ...block.style,
                            fontSize: e.target.value ? `${e.target.value}px` : undefined,
                          },
                        },
                        path,
                      )
                    }
                  />
                </div>

                <div>
                  <label className="field-label" htmlFor="font-weight">
                    Font weight
                  </label>
                  <select
                    id="font-weight"
                    title="Select font weight"
                    className="field-input"
                    value={block.style?.fontWeight ?? ''}
                    onChange={(e) =>
                      updateBlock(
                        block.id,
                        {
                          style: { ...block.style, fontWeight: e.target.value },
                        },
                        path,
                      )
                    }
                  >
                    <option value="">Default</option>
                    <option value="100">Thin (100)</option>
                    <option value="200">Extra Light (200)</option>
                    <option value="300">Light (300)</option>
                    <option value="400">Normal (400)</option>
                    <option value="500">Medium (500)</option>
                    <option value="600">Semi Bold (600)</option>
                    <option value="700">Bold (700)</option>
                    <option value="800">Extra Bold (800)</option>
                    <option value="900">Black (900)</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {block.type === 'quote' && (
            <div>
              <label className="field-label">Citation</label>
              <input
                title="Citation input"
                type="text"
                className="field-input"
                placeholder="Author or source"
                value={block.attrs?.citation ?? ''}
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    {
                      attrs: { ...block.attrs, citation: e.target.value },
                    },
                    path,
                  )
                }
              />
            </div>
          )}

          {block.type === 'button' && (
            <ButtonInspector
              block={block}
              path={path}
              variableNameOptions={variableNameOptions ?? availableVariables}
            />
          )}

          {block.type === 'columns' && <ColumnsBlockInspector path={path} block={block} />}

          {block.type === 'image' && (
            <ImageInspector
              block={block}
              path={path}
              variableNameOptions={variableNameOptions ?? availableVariables}
            />
          )}

          {block.type === 'avatar' && (
            <AvatarInspector
              block={block}
              path={path}
              variableNameOptions={variableNameOptions ?? availableVariables}
            />
          )}

          {block.type === 'otp' && (
            <OtpInspector
              block={block}
              path={path}
              variableNameOptions={variableNameOptions ?? availableVariables}
            />
          )}
          {block.type === 'steps' && <StepsInspector block={block} path={path} />}
          {block.type === 'stat' && <StatInspector block={block} path={path} />}
          {block.type === 'table' && <TableInspector block={block} path={path} />}
          {block.type === 'html' && <HtmlBlockInspector block={block} path={path} />}
          {block.type === 'signature' && <SignatureInspector block={block} path={path} />}
          {block.type === 'list' && (
            <ListInspector
              block={block}
              path={path}
              // The block binds to a *list variable*, so this panel needs the
              // document's definitions, not just the token names.
              rawVariableDefinitions={mode === 'email' ? post?.['variableDefinition'] : undefined}
            />
          )}
        </>
      )}
    </div>
  );
}
